import React, { FunctionComponent, ReactElement, ReactNode, Ref } from 'react';
import { useTheme } from 'styled-components';

import { BUTTON_TYPES } from '@savgroup-front-common/constants/src/shared';
import { MessageType } from '@savgroup-front-common/types';

import { useNewUiContext } from '../../components/NewUiProvider/NewUiProvider';
import { safeFormattedIntlString } from '../../formatters';
import { useIsNewUiEnabled } from '../../hooks/useIsNewUiEnabled';
import { CrossIcon, LoaderIcon, SearchNewBoIcon } from '../../protons/icons';

import messages from './messages';
import {
  $SearchActionContainer,
  $SearchClearButton,
  $SearchFilter,
  $SearchInput,
  $SearchInputContainer,
} from './SearchInput.styles';
import { SearchInputVariant } from './SearchInput.types';

interface SearchInputProps {
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onClear?: React.MouseEventHandler<HTMLButtonElement>;
  placeholder?: MessageType | string;
  isLoading?: boolean;
  dataTestId?: string;
  children?: ReactNode;
  variant?: SearchInputVariant;
  isActive?: boolean;
  searchFilter?: ReactElement;
  isHightContrast?: boolean;
  isSmall?: boolean;
  shouldFocusOnMount?: boolean;
}
interface SearchInputWithRefProps extends SearchInputProps {
  forwardedRef: Ref<HTMLInputElement>;
}

const SearchInput: FunctionComponent<
  React.PropsWithChildren<SearchInputWithRefProps>
> = ({
  value,
  onChange,
  onBlur,
  onFocus,
  onClick,
  isLoading = false,
  forwardedRef,
  onKeyDown,
  onClear,
  dataTestId = 'searchInput',
  placeholder = messages.placeholder,
  children,
  variant = SearchInputVariant.DEFAULT,
  isActive,
  searchFilter,
  isHightContrast = false,
  isSmall = false,
  shouldFocusOnMount,
}) => {
  const theme = useTheme();
  const isNewUiEnabled = useIsNewUiEnabled();
  const { isDarkModeEnabled } = useNewUiContext();

  return (
    <$SearchInputContainer
      $variant={variant}
      $isNewUiEnabled={isNewUiEnabled}
      $isActive={isActive}
      $isHightContrast={isDarkModeEnabled || isHightContrast}
    >
      <$SearchInput
        $variant={variant}
        $isNewUiEnabled={isNewUiEnabled}
        placeholder={safeFormattedIntlString(placeholder)}
        onChange={onChange}
        onClick={onClick}
        onBlur={onBlur}
        value={value}
        onFocus={onFocus}
        ref={forwardedRef}
        onKeyDown={onKeyDown}
        dataTestId={`${dataTestId}_input`}
        $isHightContrast={isHightContrast}
        icon={
          isLoading ? (
            <LoaderIcon
              color={
                isDarkModeEnabled || isHightContrast
                  ? theme.colors.white
                  : theme.colors.mainTextColor
              }
              size="22px"
              strokeWidth="10"
            />
          ) : (
            <SearchNewBoIcon size="22px" />
          )
        }
        isSmall={isSmall}
        shouldFocusOnMount={shouldFocusOnMount}
      />

      <$SearchActionContainer>
        {onClear && value && (
          <$SearchClearButton
            icon={<CrossIcon />}
            naked
            type={BUTTON_TYPES.BUTTON}
            onClick={onClear}
            dataTestId={`${dataTestId}_clearButton`}
            ariaLabel={messages.clearSearchValue}
            small={isSmall}
            $isHightContrast={isHightContrast}
          />
        )}
        {children}

        {searchFilter && <$SearchFilter>{searchFilter}</$SearchFilter>}
      </$SearchActionContainer>
    </$SearchInputContainer>
  );
};

export default React.forwardRef<HTMLInputElement, SearchInputProps>(
  (props, ref) => <SearchInput forwardedRef={ref} {...props} />,
);
